/* eslint-disable react-hooks/exhaustive-deps */
import { useNavigate, useParams } from "react-router";
import {
  ControlledInput,
  ControlledSelect,
  PrimaryButton,
  SecondaryButton,
} from "../../components";
import { withProtectedRoute } from "../../utils/HOC/withProtectedRoute";
import { useForm } from "react-hook-form";
import { SketchPicker } from "react-color";
import { useEffect, useState } from "react";
import { IMarketPricing, useMarketPricing } from "../../store/useMarketPricing";
import { useUserProfileBadges } from "../../store/useUserProfileBadges";
import { UsersTable } from "./UsersTable";

type Inputs = {
  name: string;
  description: string;
  type: "FIRST_DROP" | "DROP_COUNT" | "DROP_VALUE" | "ITEM_TYPE";
  minDrops?: number;
  minValue?: number;
  itemType?: IMarketPricing;
  rewardDroppies?: number;
};

function EditUserProfileBadgeScreen() {
  /** Hooks */
  const params = useParams();
  const navigate = useNavigate();
  const {
    redeemedUsersByBadgeId,
    getRedeemedUsersByBadgeId,
    oneUserProfileBadge,
    getOneUserProfileBadge,
    updateUserProfileBadge,
    updatedUserProfileBadge,
    clearUpdatedUserProfileBadge,
  } = useUserProfileBadges();
  const { getAllMarketPrices, marketPrices } = useMarketPricing();
  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm<Inputs>();
  /** State */
  const [, /* submitted */ setSubmitted] = useState<boolean>(false);
  const [colorValue, setColorValue] = useState<string>("");

  const [color, setColor] = useState<string>("");
  const [showColorPicker, setShowColorPicker] = useState<boolean>(false);

  /** Callbacks */
  const goBack = () => navigate(-1);
  const onSubmit = (data: Inputs) => {
    // if (color === "") {
    //   setAlert({
    //     isOpen: true,
    //     message: "Please add color.",
    //     severity: "error",
    //   });
    //   setSubmitted(true);
    //   return;
    // }
    setSubmitted(false);
    const body: any = {
      ...data,
      rewardDroppies: Number(data.rewardDroppies) as number,
    };
    if (color !== "") {
      body.color = color;
    }
    updateUserProfileBadge(params?.id as string, body);
  };

  const onColorChange = (color: any) => setColorValue(color.hex);
  const onColorChangeComplete = (color: any) => {
    setColor(color.hex);
    setShowColorPicker(false);
  };

  /** Effects */
  useEffect(() => {
    if (params?.id) {
      getOneUserProfileBadge(params?.id);
      getRedeemedUsersByBadgeId(params?.id);
    }
  }, [params?.id]);

  useEffect(() => {
    if (oneUserProfileBadge) {
      reset({
        name: oneUserProfileBadge?.name,
        description: oneUserProfileBadge?.description,
        minDrops: oneUserProfileBadge?.minDrops,
        minValue: Number(oneUserProfileBadge?.minValue ?? 0) / 100,
        type: oneUserProfileBadge?.type,
        itemType: oneUserProfileBadge?.itemType,
        rewardDroppies: oneUserProfileBadge?.rewardDroppies,
      });
      setColor((oneUserProfileBadge?.color ?? "") as string);
    }
  }, [oneUserProfileBadge]);

  useEffect(() => {
    getAllMarketPrices();
  }, []);

  useEffect(() => {
    if (updatedUserProfileBadge) {
      navigate(-1);
      clearUpdatedUserProfileBadge();
    }
  }, [updatedUserProfileBadge]);

  const colorError = false;
  // (Object.values(errors)?.length && color === "") ||
  // (submitted && color === "");

  return (
    <div>
      <div className="flex flex-row items-center mb-6">
        <h1 className="font-satoshi text-2xl font-bold text-dark">
          Add new user profile badge
        </h1>
      </div>
      <div className="flex flex-col sm:flex-row">
        <div className="w-full sm:w-1/2 bg-white border border-gray-border drop-shadow-md mb-1 sm:mb-0">
          <div className="w-full border-b border-gray-border px-[28px] py-[15px]">
            <p className="font-satoshi font-medium text-base text-dark">
              New user profile badge
            </p>
          </div>
          <form onSubmit={handleSubmit(onSubmit)} className="w-full px-7 py-7">
            {/* Name */}
            <ControlledInput
              name="name"
              rules={{ required: true }}
              control={control}
              error={Boolean(errors?.name)}
              label="Name"
              type="text"
              placeholder="Enter name here"
            />
            {/* Description */}
            <ControlledInput
              name="description"
              rules={{ required: true }}
              control={control}
              error={Boolean(errors?.description)}
              label="Description"
              type="text"
              placeholder="Enter description here"
            />
            {/* Badge Type */}
            <ControlledSelect
              name="type"
              control={control}
              rules={{ required: true }}
              error={Boolean(errors?.type)}
              label="Badge type"
              options={types}
              placeholder="Pick badge type here"
            />
            {/* Min Drops */}
            {watch("type") === "DROP_COUNT" ? (
              <ControlledInput
                name="minDrops"
                rules={{ required: true }}
                control={control}
                error={Boolean(errors?.minDrops)}
                label="Min Drops"
                type="number"
                placeholder="Enter min drops here"
              />
            ) : null}
            {/* Min value */}
            {watch("type") === "DROP_VALUE" ? (
              <ControlledInput
                name="minValue"
                rules={{ required: true }}
                control={control}
                error={Boolean(errors?.minValue)}
                label="Min Value"
                type="number"
                placeholder="Enter min value of drop here"
              />
            ) : null}
            {watch("type") === "ITEM_TYPE" ? (
              <ControlledSelect
                name="itemType"
                control={control}
                rules={{ required: true }}
                error={Boolean(errors?.itemType)}
                label="Composition type"
                options={marketPrices?.map((price) => ({
                  _id: price?._id as string,
                  title: price?.name as string,
                }))}
                placeholder="Pick item type here"
              />
            ) : null}
            {/* Color */}
            <div className="mb-4">
              <p className={`font-satoshi text-dark text-sm font-medium mb-3`}>
                Add color
              </p>
              <div
                className={`w-full h-[46px] px-4 bg-sky-lighter border ${
                  colorError ? "border-red-base" : "border-gray-border"
                } ${
                  colorError
                    ? "focus:border-red-base"
                    : "focus:border-focused-border"
                } rounded font-satoshi text-base text-dark font-medium outline-none flex flex-row items-center justify-between`}
              >
                <div className="flex flex-row items-center w-[100%] h-[100%]">
                  {color !== "" ? (
                    <div
                      onClick={() => {
                        setColor("");
                        setShowColorPicker(false);
                      }}
                      style={{ backgroundColor: color }}
                      className={`w-[15px] h-[15px] mr-1 cursor-pointer rounded`}
                    />
                  ) : null}
                </div>
                <button
                  onClick={() => setShowColorPicker(!showColorPicker)}
                  type="button"
                  className="px-[8px] py-[3px] bg-primary-base font-satoshi font-medium text-white text-sm rounded"
                >
                  {color !== "" ? "Edit" : "Add"}
                </button>
              </div>
            </div>
            {showColorPicker ? (
              <SketchPicker
                color={colorValue}
                onChangeComplete={onColorChangeComplete}
                onChange={onColorChange}
              />
            ) : null}
            {/* Reward droppies */}
            <ControlledInput
              name="rewardDroppies"
              rules={{ required: true, pattern: /^[0-9]*$/ }}
              control={control}
              error={Boolean(errors?.rewardDroppies)}
              label="Reward droppies"
              type="number"
              placeholder="Type reward droppies here"
            />
            <div className="flex flex-row items-center justify-end">
              <SecondaryButton type="button" onClick={goBack} label="Cancel" />
              <PrimaryButton type="submit" className="ml-4" label="Save" />
            </div>
          </form>
        </div>
      </div>

      <div className="mt-12">
        <div className="flex w-full justify-between mb-3">
          Users that have already redeemed this badge
        </div>
        <div className="w-full px-7 py-7 bg-white rounded-xl border-gray-border drop-shadow-none">
          {!redeemedUsersByBadgeId?.length ? (
            <p className="text-center font-satoshi text-base text-dark">
              There is no user badge created yet.
            </p>
          ) : (
            <UsersTable />
          )}
        </div>
      </div>
    </div>
  );
}

export default withProtectedRoute(EditUserProfileBadgeScreen);

const types = [
  {
    _id: "FIRST_DROP",
    title: "First Drop",
  },
  {
    _id: "DROP_COUNT",
    title: "Drop Count",
  },
  {
    _id: "DROP_VALUE",
    title: "Drop Value",
  },
  {
    _id: "ITEM_TYPE",
    title: "Item Type",
  },
];
